<template>
  <b-card class="create-rule" id="edit-rule-card">
    <div slot="header">Modify Rule</div>
    <div>
      <div class="row mb-3">
        <div class="col-xl-8 col-lg-12">
          <label for="devices">Enabled: </label>
          <label class="switch switch-sm switch-text switch-info mb-0" style="margin-left: 5px;">
            <input class="switch-input" type="checkbox" v-model="rule.enabled">
            <span data-on="Yes" data-off="No" class="switch-label"></span>
            <span class="switch-handle"></span>
          </label>
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-xl-8 col-lg-12">
          <label for="devices">Devices: </label>
          <multiselect
            v-if="!loading"
            v-model="rule.devices"
            :options="groupedDevices"
            :multiple="true"
            group-values="devices"
            group-label="type"
            :group-select="true"
            placeholder="Select Devices"
            track-by="name"
            label="name"
            :closeOnSelect="true"
          ><span slot="noResult">Oops! No elements found. Consider changing the search query.</span>
          </multiselect>
        </div>
      </div>

      <div class="rule-conditions">
        <div class="row">
          <div class="col-xl-4 col-lg-6 col-md-12">
            <label for="rule-label">Alert Name: </label>
            <b-form-input name="rule-label" v-model.trim.lazy="rule.label" v-validate="'required|min:3|max:32'" :class="{'is-invalid': errors.has('rule-label') }" />
            <b-form-text class="is-invalid help" v-show="errors.has('rule-label')">
              {{ errors.first('rule-label') }}
            </b-form-text>
          </div>
        </div>

        <div class="row">
          <div class="col-xl-8 col-lg-12">
            <label for="devices">Send Alerts To: </label>
            <multiselect
              v-if="targets.length"
              v-model="rule.targets"
              :options="targets"
              :multiple="true"
              placeholder="Select Alert Targets"
              track-by="id"
              label="label"
              :custom-label="targetLabel"
              :closeOnSelect="true"
            ><span slot="noResult">Oops! No tagets found.</span>
            </multiselect>
            <div v-else>No targets found. <router-link :to="{ name: 'Notification Targets' }">Create one</router-link> or continue to show alerts on dashboard only.</div>
          </div>
        </div>

        <div class="row">
          <div class="col-xl-2 col-lg-3 col-md-6">
            <label for="operator">Aggregation: </label>
            <b-form-select v-model="rule.aggPeriod">
              <option value="300">5 Minute Avg.</option>
              <option value="600">10 Minute Avg.</option>
              <option value="900">15 Minute Avg.</option>
            </b-form-select>
          </div>
          <div class="col-xl-2 col-lg-3 col-md-6">
            <label for="metric">Metric: </label>
            <b-form-select
              name="rule-metric"
              v-model="rule.metricName"
              v-validate="'required'"
               :class="{'is-invalid': errors.has('rule-metric') }"
            >
              <option
                v-for="dm in deviceMetrics[deviceType]"
                :value="dm.value"
                :key="dm.value">{{ dm.text }}</option>
            </b-form-select>
            <b-form-text class="is-invalid help" v-show="errors.has('rule-metric')">
              {{ errors.first('rule-metric') }}
            </b-form-text>
          </div>
          <div class="col-xl-2 col-lg-3 col-md-6">
            <label for="operator">Operator: </label>
            <b-form-select v-model="rule.comparator">
              <option value=">">Greater Than</option>
              <option value="<">Less Than</option>
            </b-form-select>
          </div>
          <div class="col-xl-2 col-lg-3 col-md-6">
            <label for="value">Value: </label>
            <b-form-input name="rule-value" v-model.trim.lazy="rule.triggerValue" v-validate="'required|numeric'" :class="{'is-invalid': errors.has('rule-value') }" />
            <b-form-text class="is-invalid help" v-show="errors.has('rule-value')">
              {{ errors.first('rule-value') }}
            </b-form-text>
          </div>
        </div>
        <div class="row">
          <div class="col-xl-2 col-lg-3 col-md-6">
            <label for="alertReminder">Alert Reminder: </label>
            <b-form-select v-model="rule.reminderInterval">
              <option value="0">Never</option>
              <option value="3600">Every Hour</option>
              <option value="21600">Every 6 Hours</option>
              <option value="43200">Every 12 Hours</option>
              <option value="86400">Daily</option>
            </b-form-select>
          </div>
          <div class="col-xl-2 col-lg-3 col-md-6">
            <label for="alertExpiration">Alert Expiration: </label>
            <b-form-select v-model="rule.expiresIn">
              <option value="0">Never</option>
              <option value="86400">1 Day</option>
              <option value="259200">3 Days</option>
              <option value="604800">1 Week</option>
            </b-form-select>
          </div>
          <div class="col-lg-3">
            <b-button class="rule-submit" type="submit" variant="success" @click.prevent="saveRule"><i class="fas fa-check"></i><span class="pl-2">Save</span></b-button>
            <b-button class="rule-submit ml-1" type="button" variant="danger" @click="$emit('close')"><i class="fas fa-times"></i><span class="pl-2">Cancel</span></b-button>
          </div>
        </div>
      </div>

    </div>
  </b-card>
</template>

<script>
import { Validator } from 'vee-validate'
import Multiselect from 'vue-multiselect'
import { DEVICES_QUERY, UPDATE_RULE_MUTATION, TARGETS_QUERY } from '@/constants/graphql'

const dictionary = {
  en: {
    attributes: {
      'rule-value': 'value',
      'rule-label': 'rule name'
    }
  }
}

Validator.localize(dictionary)

export default {
  name: 'rules',
  props: {
    rule: {
      type: Object,
      default () {
        return {
          enabled: true
        }
      }
    }
  },
  data () {
    return {
      loading: 0,
      deviceMetrics: {
        plunge: [
          {
            value: 'ec',
            text: 'Conductivity',
            type: 'float'
          },
          {
            value: 'tds',
            text: 'TDS',
            type: 'float'
          },
          {
            value: 'temp_c',
            text: 'Temp. Celsius',
            type: 'float'
          },
          {
            value: 'temp_f',
            text: 'Temp. Fahrenheit',
            type: 'float'
          }
        ]
      }
    }
  },
  computed: {
    deviceType () {
      return (this.devices.length) ? this.devices[0].type : 'plunge'
    },
    groupedDevices () {
      let groups = {}
      let options = []

      if (this.devices) {
        this.devices.forEach((device) => {
          let d = groups.hasOwnProperty(this.ucfirst(device.type)) ? groups[this.ucfirst(device.type)] : groups[this.ucfirst(device.type)] = []
          d.push({ name: `${device.site.name} - ${device.name}`, serial: device.serial, type: device.type, id: device.id })
        })
        for (let type in groups) {
          if (groups.hasOwnProperty(type)) {
            options.push({ type, devices: groups[type] })
          }
        }
      }
      return options
    }
  },
  created () {
  },
  methods: {
    targetLabel (target) {
      return `${target.label} - ${target.destination}`
    },
    ucfirst (str) {
      return str.charAt(0).toUpperCase() + str.substr(1)
    },
    saveRule () {
      this.$validator.validateAll().then((result) => {
        if (!this.rule.devices.length) {
          this.$swal.fire({ icon: 'warning', title: 'Please select at least one device.' })
          result = false
        }

        if (result) {
          let devices = this.rule.devices.map(d => { return { serial: d.serial } })
          let targets = this.rule.targets.map(d => { return { id: d.id } })

          let data = { ...this.rule }
          data.devices = {
            connect: devices
          }
          data.targets = {
            connect: targets
          }

          data.aggPeriod = parseInt(data.aggPeriod)
          data.triggerValue = parseFloat(data.triggerValue)
          data.reminderInterval = parseInt(data.reminderInterval)
          data.expiresIn = parseInt(data.expiresIn)
          delete data.id
          delete data.__typename

          this.$apollo.mutate({
            mutation: UPDATE_RULE_MUTATION,
            variables: { data, where: { id: this.rule.id } }
          }).then(() => {
            this.$swal.fire({ icon: 'success', title: 'Success! Site Updated' })
            this.$emit('saved')
          }).catch(() => {
            // handled by global onError handler but still need to catch in the promise
          })
        }
      })
    }
  },
  apollo: {
    targets: {
      query: TARGETS_QUERY
    },
    devices: {
      query: DEVICES_QUERY
    }
  },
  components: {
    Multiselect
  }
}
</script>

<style scoped>
.rule-conditions .row {
  margin-bottom: 20px;
}
.rule-submit {
  margin-top: 28px;
}
</style>
